/**
 * App session persistence via localStorage (shared across tabs, survives tab close).
 * Do not use sessionStorage for app state — use this module instead.
 */

const canUseStorage = () => typeof window !== "undefined" && Boolean(window.localStorage);

export const APP_SESSION_KEYS = [
    "user_id",
    "email",
    "user_email",
    "phone_number",
    "user_phone",
    "country_code",
    "user_name",
    "email_verified",
    "sms_verified",
    "verification_type",
    "payment_status",
    "plan_status",
    "plan_type",
    "choose_plan_required",
    "upgrade_plan_required",
    "last_active_sidebar_path",
    "active_section",
    "active_setting_subsection",
    "active_permission_subtab",
    "active_section_friend",
    "pending_profile_completion_step",
    "unread_notification_count",
    "has_new_notification",
    "phonebook_tour_shown_session_v1",
    "phonebook_tour_step_v1",
    "phonebook_tour_pending_first_v1",
    "phonebook_post_signup_intro_active_v1",
    "phonebook_post_signup_await_contact_v1",
];

const MIGRATION_FLAG = "__app_storage_migrated_from_session_v1";

export const appGetItem = (key) => {
    try {
        if (!canUseStorage()) return null;
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

export const appSetItem = (key, value) => {
    try {
        if (!canUseStorage()) return;
        localStorage.setItem(key, String(value));
    } catch {
        // ignore storage errors
    }
};

export const appRemoveItem = (key) => {
    try {
        if (!canUseStorage()) return;
        localStorage.removeItem(key);
    } catch {
        // ignore storage errors
    }
};

export const clearAppSession = () => {
    APP_SESSION_KEYS.forEach((key) => appRemoveItem(key));
};

export const migrateSessionStorageToLocalStorage = () => {
    if (!canUseStorage() || typeof window.sessionStorage === "undefined") return;
    if (appGetItem(MIGRATION_FLAG) === "1") return;

    try {
        for (let i = 0; i < sessionStorage.length; i += 1) {
            const key = sessionStorage.key(i);
            if (!key) continue;
            const value = sessionStorage.getItem(key);
            if (value != null && appGetItem(key) == null) {
                localStorage.setItem(key, value);
            }
        }
        sessionStorage.clear();
        appSetItem(MIGRATION_FLAG, "1");
    } catch {
        // ignore migration errors
    }
};

/** Drop-in replacement API (localStorage-backed). */
export const appStorage = {
    getItem: appGetItem,
    setItem: appSetItem,
    removeItem: appRemoveItem,
    clear: clearAppSession,
};

export default appStorage;
